import CobraSpinner from '../shared/CobraSpinner';
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  VStack,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  Divider,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  useBreakpointValue,
  Progress,
  Image,
  Tooltip,
  Container
} from '@chakra-ui/react';
import {
  SearchIcon,
  TimeIcon,
  CheckCircleIcon,
  WarningIcon,
  ChevronDownIcon,
  StarIcon,
  ViewIcon
} from '@chakra-ui/icons';
import {
  FiBookOpen,
  FiClock,
  FiUser,
  FiPlay,
  FiAward,
  FiTarget,
  FiTrendingUp,
  FiRefreshCw,
  FiFilter,
  FiEye,
  FiBarChart,
  FiUsers,
  FiCalendar
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../shared/Sidebar';
import { SidebarProvider, useSidebar } from '../utils/SidebarContext';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const QCMCard = ({ qcm, onStart, primaryColor, secondaryColor, textColor, cardBg }) => {
  const navigate = useNavigate();
  
  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'green';
      case 'medium': return 'yellow';
      case 'hard': return 'red';
      default: return 'blue';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'green';
      case 'in_progress': return 'yellow';
      case 'not_started': return 'gray';
      default: return 'blue';
    }
  };

  return (
    <motion.div variants={fadeIn}>
      <Card
        bg={cardBg}
        borderRadius="xl"
        boxShadow="md"
        border="1px solid"
        borderColor="gray.200"
        _hover={{
          transform: "translateY(-4px)",
          boxShadow: "xl",
          borderColor: primaryColor
        }}
        transition="all 0.3s ease"
        overflow="hidden"
      >
        <CardHeader pb={2}>
          <Flex justify="space-between" align="flex-start">
            <VStack align="flex-start" spacing={1} flex="1">
              <Heading size="md" color={secondaryColor} fontFamily="Tajawal, sans-serif">
                {qcm.title || 'اختبار بدون عنوان'}
              </Heading>
              <Text fontSize="sm" color="gray.600">
                {qcm.description || 'لا يوجد وصف متاح'}
              </Text>
            </VStack>
            <Badge
              colorScheme={getDifficultyColor(qcm.difficulty)}
              variant="subtle"
              borderRadius="full"
              px={3}
              py={1}
            >
              {qcm.difficulty || 'متوسط'}
            </Badge>
          </Flex>
        </CardHeader>

        <CardBody pt={0}>
          <VStack spacing={4} align="stretch">
            {/* QCM Stats */}
            <SimpleGrid columns={3} spacing={4}>
              <Stat textAlign="center">
                <StatLabel fontSize="xs" color="gray.500">الأسئلة</StatLabel>
                <StatNumber fontSize="lg" color={secondaryColor}>
                  {qcm.questions?.length || 0}
                </StatNumber>
              </Stat>
              <Stat textAlign="center">
                <StatLabel fontSize="xs" color="gray.500">المدة</StatLabel>
                <StatNumber fontSize="lg" color={secondaryColor}>
                  {qcm.duration || 30} د
                </StatNumber>
              </Stat>
              <Stat textAlign="center">
                <StatLabel fontSize="xs" color="gray.500">النقاط</StatLabel>
                <StatNumber fontSize="lg" color={secondaryColor}>
                  {qcm.totalPoints || qcm.questions?.length * 10 || 100}
                </StatNumber>
              </Stat>
            </SimpleGrid>

            <Divider />

            {/* QCM Info */}
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <HStack>
                  <Icon as={FiUser} color="gray.500" />
                  <Text fontSize="sm" color="gray.600">
                    {qcm.instructor || 'غير محدد'}
                  </Text>
                </HStack>
                <Badge
                  colorScheme={getStatusColor(qcm.status)}
                  variant="outline"
                  borderRadius="full"
                >
                  {qcm.status === 'completed' ? 'مكتمل' : 
                   qcm.status === 'in_progress' ? 'قيد التقدم' : 'لم يبدأ'}
                </Badge>
              </HStack>

              {qcm.lastAttempt && (
                <HStack>
                  <Icon as={FiClock} color="gray.500" />
                  <Text fontSize="sm" color="gray.600">
                    آخر محاولة: {format(new Date(qcm.lastAttempt), 'dd/MM/yyyy', { locale: ar })}
                  </Text>
                </HStack>
              )}

              {qcm.bestScore && (
                <HStack>
                  <Icon as={FiAward} color={primaryColor} />
                  <Text fontSize="sm" color={primaryColor} fontWeight="bold">
                    أفضل نتيجة: {qcm.bestScore}%
                  </Text>
                </HStack>
              )}
            </VStack>

            {/* Action Buttons */}
            <HStack spacing={2}>
              <Button
                flex="1"
                bg={primaryColor}
                color={secondaryColor}
                _hover={{ bg: `${primaryColor}dd` }}
                leftIcon={<FiPlay />}
                onClick={() => onStart(qcm)}
                fontFamily="Tajawal, sans-serif"
                fontWeight="bold"
              >
                {qcm.status === 'completed' ? 'إعادة المحاولة' : 'بدء الاختبار'}
              </Button>
              <IconButton
                aria-label="عرض التفاصيل"
                icon={<FiEye />}
                variant="outline"
                borderColor="gray.300"
                _hover={{ borderColor: primaryColor, color: primaryColor }}
                onClick={() => navigate(`/qcm/${qcm._id}/details`)}
              />
              {qcm.status === 'completed' && (
                <IconButton
                  aria-label="عرض النتائج"
                  icon={<FiBarChart />}
                  variant="outline"
                  borderColor="gray.300"
                  _hover={{ borderColor: primaryColor, color: primaryColor }}
                  onClick={() => navigate(`/qcm/${qcm._id}/results`)}
                />
              )}
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </motion.div>
  );
};

const QCMListContent = () => {
  const { isOpen } = useSidebar();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const toast = useToast();
  const contentMarginRight = useBreakpointValue({ base: '0px', md: isOpen ? '280px' : '80px' });
  
  // Platform colors
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('#2C3E50', 'white');

  const [qcms, setQcms] = useState([]);
  const [filteredQcms, setFilteredQcms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    notStarted: 0,
    averageScore: 0
  });

  useEffect(() => {
    fetchQCMs();
  }, []);

  useEffect(() => {
    filterQCMs();
  }, [qcms, searchTerm, filterStatus, filterDifficulty]);

  const fetchQCMs = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/qcm/tests/qcm/me');
      const qcmData = response.data || [];
      
      setQcms(qcmData);
      calculateStats(qcmData);
    } catch (error) {
      console.error('خطأ في جلب الاختبارات:', error);
      toast({
        title: 'خطأ في تحميل البيانات',
        description: 'فشل في تحميل قائمة الاختبارات',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const calculateStats = (qcmData) => {
    const total = qcmData.length;
    const completed = qcmData.filter(q => q.status === 'completed').length;
    const inProgress = qcmData.filter(q => q.status === 'in_progress').length;
    const notStarted = qcmData.filter(q => q.status === 'not_started' || !q.status).length;
    
    const scores = qcmData.filter(q => q.bestScore).map(q => q.bestScore);
    const averageScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;

    setStats({
      total,
      completed,
      inProgress,
      notStarted,
      averageScore: Math.round(averageScore)
    });
  };

  const filterQCMs = () => {
    let filtered = qcms;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(qcm =>
        qcm.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        qcm.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        qcm.instructor?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(qcm => qcm.status === filterStatus);
    }

    // Difficulty filter
    if (filterDifficulty !== 'all') {
      filtered = filtered.filter(qcm => qcm.difficulty === filterDifficulty);
    }

    setFilteredQcms(filtered);
  };

  const handleStartQCM = (qcm) => {
    navigate(`/qcm/${qcm._id}/take`);
  };

  if (isLoading) {
    return (
      <Flex h="100vh" align="center" justify="center" dir="rtl">
        <VStack spacing={4}>
          <CobraSpinner size="xl" color={primaryColor} thickness="4px" />
          <Text color={textColor} fontFamily="Tajawal, sans-serif">جاري تحميل الاختبارات...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box dir="rtl" fontFamily="Tajawal, sans-serif">
      <Flex h="100vh" overflowX="hidden" flexDirection="column">
        <Sidebar />

        <Box
          flex="1"
          mr={contentMarginRight}
          p={6}
          bg={bgColor}
          transition="margin-right 0.3s ease"
          overflowY="auto"
        >
          {/* Header */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={6}>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={4}
                >
                  <VStack align="start" spacing={2}>
                    <Heading 
                      as="h1" 
                      size="xl" 
                      color={secondaryColor} 
                      fontFamily="Tajawal, sans-serif"
                      fontWeight="700"
                    >
                      الاختبارات والتقييمات 📝
                    </Heading>
                    <Text color="gray.600" fontSize="md">
                      اختبر معلوماتك وقيّم تقدمك في المواد الدراسية
                    </Text>
                  </VStack>
                  
                  <Button
                    bg={primaryColor}
                    color={secondaryColor}
                    _hover={{ bg: `${primaryColor}dd` }}
                    leftIcon={<FiRefreshCw />}
                    onClick={fetchQCMs}
                    fontFamily="Tajawal, sans-serif"
                    fontWeight="bold"
                  >
                    تحديث القائمة
                  </Button>
                </Flex>
              </CardBody>
            </Card>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} mb={8}>
              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat textAlign="center">
                      <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                        إجمالي الاختبارات
                      </StatLabel>
                      <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                        {stats.total}
                      </StatNumber>
                      <StatHelpText color="blue.500" fontSize="sm">
                        متاحة للحل
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat textAlign="center">
                      <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                        مكتملة
                      </StatLabel>
                      <StatNumber color="green.500" fontSize="2xl" fontWeight="bold">
                        {stats.completed}
                      </StatNumber>
                      <StatHelpText color="green.500" fontSize="sm">
                        <StatArrow type="increase" />
                        تم إنجازها
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat textAlign="center">
                      <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                        قيد التقدم
                      </StatLabel>
                      <StatNumber color={primaryColor} fontSize="2xl" fontWeight="bold">
                        {stats.inProgress}
                      </StatNumber>
                      <StatHelpText color={primaryColor} fontSize="sm">
                        جاري العمل عليها
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat textAlign="center">
                      <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                        متوسط النتائج
                      </StatLabel>
                      <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                        {stats.averageScore}%
                      </StatNumber>
                      <StatHelpText color="blue.500" fontSize="sm">
                        الأداء العام
                      </StatHelpText>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>
            </SimpleGrid>
          </motion.div>

          {/* Filters */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={6}>
                <Flex
                  gap={4}
                  flexDirection={{ base: 'column', md: 'row' }}
                  align={{ base: 'stretch', md: 'center' }}
                >
                  <InputGroup flex="1" maxW="400px">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.600" />
                    </InputLeftElement>
                    <Input 
                      placeholder="البحث في الاختبارات..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      bg="gray.50"
                      border="2px solid"
                      borderColor="gray.200"
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`
                      }}
                      borderRadius="lg"
                    />
                  </InputGroup>
                  
                  <Menu>
                    <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="outline">
                      الحالة: {filterStatus === 'all' ? 'الكل' : 
                               filterStatus === 'completed' ? 'مكتمل' :
                               filterStatus === 'in_progress' ? 'قيد التقدم' : 'لم يبدأ'}
                    </MenuButton>
                    <MenuList>
                      <MenuItem onClick={() => setFilterStatus('all')}>الكل</MenuItem>
                      <MenuItem onClick={() => setFilterStatus('completed')}>مكتمل</MenuItem>
                      <MenuItem onClick={() => setFilterStatus('in_progress')}>قيد التقدم</MenuItem>
                      <MenuItem onClick={() => setFilterStatus('not_started')}>لم يبدأ</MenuItem>
                    </MenuList>
                  </Menu>

                  <Menu>
                    <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="outline">
                      الصعوبة: {filterDifficulty === 'all' ? 'الكل' : 
                                filterDifficulty === 'easy' ? 'سهل' :
                                filterDifficulty === 'medium' ? 'متوسط' : 'صعب'}
                    </MenuButton>
                    <MenuList>
                      <MenuItem onClick={() => setFilterDifficulty('all')}>الكل</MenuItem>
                      <MenuItem onClick={() => setFilterDifficulty('easy')}>سهل</MenuItem>
                      <MenuItem onClick={() => setFilterDifficulty('medium')}>متوسط</MenuItem>
                      <MenuItem onClick={() => setFilterDifficulty('hard')}>صعب</MenuItem>
                    </MenuList>
                  </Menu>
                </Flex>
              </CardBody>
            </Card>
          </motion.div>

          {/* QCM Grid */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {filteredQcms.length === 0 ? (
              <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                <CardBody p={8}>
                  <VStack spacing={4}>
                    <Icon as={FiTarget} w={12} h={12} color="gray.600" />
                    <Heading size="md" color="gray.500" textAlign="center">
                      لا توجد اختبارات متاحة
                    </Heading>
                    <Text color="gray.600" textAlign="center">
                      {searchTerm || filterStatus !== 'all' || filterDifficulty !== 'all' 
                        ? 'لم يتم العثور على اختبارات تطابق معايير البحث'
                        : 'لم يتم إضافة أي اختبارات بعد'}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {filteredQcms.map((qcm) => (
                  <QCMCard
                    key={qcm._id}
                    qcm={qcm}
                    onStart={handleStartQCM}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    textColor={textColor}
                    cardBg={cardBg}
                  />
                ))}
              </SimpleGrid>
            )}
          </motion.div>
        </Box>
      </Flex>
    </Box>
  );
};

const QCMList = () => {
  return (
    <SidebarProvider>
      <QCMListContent />
    </SidebarProvider>
  );
};

export default QCMList;


